<template>
  <button
    :type="type"
    :class="classes"
    :style="styles"
    :value="attrValue"
    @click="click"
  >
    <div class="s-btn__content">
      <slot />
    </div>
  </button>
</template>

<script>
/**
 * Highly customizable button for Shizen
 *
 * @vue-prop {Boolean} [absolute=false] - Position the element absolutely
 * @vue-prop {Boolean} [block=false] - Expands the button to 100% of available space
 * @vue-prop {String} [color] - Applies specified color to the control
 * @vue-prop {Boolean} [dark=false] - Applies the dark theme variant
 * @vue-prop {Boolean} [disabled=false] - Disable events and grey-out the button
 * @vue-prop {Boolean} [fixed=false] - Position the element fixed
 * @vue-prop {Boolean} [flat=false] - Removes the button background color
 * @vue-prop {Boolean} [floating=false] - Makes button round
 * @vue-prop {Boolean} [icon] - Designates the button as icon ; round and flat
 * @vue-prop {Boolean} [large=false] - Large size button
 * @vue-prop {Boolean} [outline=false] - Button will have an outline
 * @vue-prop {Boolean} [depressed=false] - Removes the button box shadow
 * @vue-prop {Boolean} [round=false] - Button will be round on the sides
 * @vue-prop {Boolean} [small=false] - Small size button
 * @vue-prop {String} [type='button'] - Set the button's type attribute
 * @vue-prop {} [inputValue] - Controls the button active state
 * @vue-prop {} [value] - Set the button's value attribute
 */
export default {
  name: 'SBtn',
  props: {
    absolute: Boolean,
    block: Boolean,
    color: String,
    dark: Boolean,
    disabled: Boolean,
    flat: Boolean,
    floating: Boolean,
    fixed: Boolean,
    icon: Boolean,
    large: Boolean,
    outline: Boolean,
    depressed: Boolean,
    round: Boolean,
    small: Boolean,
    type: {
      type: String,
      default: 'button',
    },
    inputValue: null,
    value: null,
  },
  data() {
    return {
      isActive: !!this.value,
    };
  },
  computed: {
    attrValue() {
      return ['string', 'number'].includes(typeof this.value)
        ? this.value
        : JSON.stringify(this.value);
    },
    needBgColor() {
      return (!this.outline && !this.flat && !this.disabled);
    },
    isDark() {
      if (this.dark === true) {
        return true;
      }
      return false;
    },
    classes() {
      return {
        's-btn': true,
        's-btn--active': this.isActive,
        's-btn--absolute': this.absolute,
        's-btn--block': this.block,
        's-btn--disabled': this.disabled,
        's-btn--flat': this.flat,
        's-btn--floating': this.floating,
        's-btn--fixed': this.fixed,
        's-btn--icon': this.icon,
        's-btn--large': this.large,
        's-btn--outline': this.outline,
        's-btn--depressed': (this.depressed && !this.flat) || this.outline,
        's-btn--round': this.round,
        's-btn--small': this.small,
        'theme--dark': this.isDark,
        'theme--light': !this.isDark,
      };
    },
    styles() {
      return {
        'background-color': this.needBgColor ? `${this.color}` : null,
        'border-color': this.needBgColor ? `${this.color}` : null,
        color: this.needBgColor ? null : `${this.color}`,
        'caret-color': this.needBgColor ? null : `${this.color}`,
      };
    },
  },
  watch: {
    inputValue(val) {
      this.isActive = !!val;
    },
    isActive(val) {
      if (!!val !== this.inputValue) {
        this.$emit('input', val);
      }
    },
  },
  methods: {
    click(e) {
      if (!this.floating && e.detail) {
        this.$el.blur();
      }
      this.$emit('click', e);
    },
  },
};
</script>

<style lang="scss">
@import "@/assets/scss/vars.scss";
@import "@/assets/scss/elevations.scss";


$button-border-radius: 2px;
$button-color: #fff;
$button-background-color: #999999;
$button-height: 36px;
$button-font-size: inherit;
$button-font-weight: 500;
$button-margin-x: 6px;
$button-margin-y: 8px;
$button-margin: $button-margin-x $button-margin-y;
$button-min-width: 88px;
$button-small-padding: 0 8px;
$button-padding: 0 16px;
$button-large-padding: 0 32px;

$button-small-height: 28px;
$button-small-font-size: inherit;

$button-large-height: 44px;
$button-large-font-size: inherit;

$button-floating-width: 56px;
$button-floating-small-width: 40px;
$button-floating-large-width: 72px;

$button-text-transform: none;

/** Nullify default <button> props */
.s-btn {
  overflow: visible;
  font: inherit;
  background-color: transparent;
  border-style: none;
  color: inherit;
  cursor: pointer;
}

/** Themes */
.theme--light.s-btn {
  color: rgba(0, 0, 0, .87);

  &.s-btn--disabled {
    color: rgba(0, 0, 0, .26) !important;

    .icon {
      color: rgba(0, 0, 0, .26) !important;
    }
    &:not(.s-btn--icon):not(.s-btn--flat):not(.s-btn--outline) {
      background-color: rgba(0, 0, 0, .12) !important;
    }
  }
  &:not(.s-btn--icon):not(.s-btn--flat) {
    background-color: #f5f5f5;
  }
}
.theme--dark.s-btn {
  color: #fff;

  &.s-btn--disabled {
    color: rgba(#fff, .3) !important;

    .icon {
      color: rgba(#fff, .3) !important;
    }
    &:not(.s-btn--icon):not(.s-btn--flat):not(.s-btn--outline) {
      background-color: rgba(#fff, .12) !important;
    }
  }
  &:not(.s-btn--icon):not(.s-btn--flat) {
    background-color: #212121;
  }
}

/** Base */
.s-btn {
  align-items: center;
  border-radius: $button-border-radius;
  display: inline-flex;
  height: $button-height;
  flex: 0 0 auto;
  font-size: $button-font-size;
  font-weight: $button-font-weight;
  justify-content: center;
  margin: $button-margin;
  min-width: $button-min-width;
  outline: 0;
  text-transform: $button-text-transform;
  text-decoration: none;
  transition: $primary-transition, color 1ms;
  position: relative;
  vertical-align: middle;
  user-select: none;
}

/** Pseudo */
.s-btn {
  &:before {
    border-radius: inherit;
    color: inherit;
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    height: 100%;
    opacity: .12;
    transition: $primary-transition;
    width: 100%;
  }
}

/** Content */
.s-btn {
  padding: $button-padding;

  &--active,
  &:hover,
  &:focus {
    position: relative;

    &:before {
      background-color: currentColor;
    }
  }

  &:hover::before {
    @media (hover: none) {
      background-color: transparent
    }
  }

  &__content {
    align-items: center;
    border-radius: inherit;
    color: inherit;
    display: flex;
    flex: 1 0 auto;
    justify-content: center;
    margin: 0 auto;
    position: relative;
    transition: $primary-transition;
    white-space: nowrap;
    width: inherit;
  }
}

/** Sizes */
.s-btn {
  &--small {
    font-size: $button-small-font-size;
    height: $button-small-height;
    padding: $button-small-padding;
  }

  &--large {
    font-size: $button-large-font-size;
    height: $button-large-height;
    padding: $button-large-padding;
  }
}

/** Icon */
.s-btn .s-btn__content .icon {
  color: inherit;
}

/** Types */
.s-btn {
  &:not(.s-btn--depressed):not(.s-btn--flat) {
    @include elevation(2);

    &:active {
      @include elevation(8);
    }
  }

  &--icon {
    background: transparent;
    box-shadow: none !important;
    border-radius: 50%;
    justify-content: center;
    min-width: 0;
    width: $button-height;

    &.s-btn--small {
      width: $button-small-height;
    }
    &.s-btn--large {
      width: $button-large-height;
    }
    &:before {
      border-radius: 50%;
    }
  }

  &--floating {
    border-radius: 50%;
    min-width: 0;
    height: $button-floating-width;
    width: $button-floating-width;
    padding: 0;

    &.s-btn--fixed,
    &.s-btn--absolute {
      z-index: 10004;
    }

    &:not(.s-btn--depressed):not(.s-btn--flat) {
      @include elevation(6);

      &:active {
        @include elevation(12);
      }
    }

    .s-btn__content {
      flex: 1 1 auto;
      margin: 0;
      height: 100%;
    }

    &:after {
      border-radius: 50%;
    }

    .icon {
      height: inherit;
      width: inherit;
    }

    &.s-btn--small {
      height: $button-floating-small-width;
      width: $button-floating-small-width;
    }

    &.s-btn--large {
      height: $button-floating-large-width;
      width: $button-floating-large-width;
    }
  }

  &--fixed, &--absolute {
    margin: 0;
  }

  &.s-btn--fixed {
    position: fixed;
  }

  &.s-btn--absolute {
    position: absolute;
  }
}

/** Disabled */
.s-btn {
  &.s-btn--disabled {
    box-shadow: none !important;
    pointer-events: none;
  }
  &:not(.s-btn--disabled):not(.s-btn--floating):not(.s-btn--icon) {
    .s-btn__content .icon {
      transition: none;
    }
  }
}

.s-btn--icon {
  padding: 0;
}

/** Custom buttons */
.s-btn {
  &.s-btn--outline {
    border: 1px solid currentColor;
    background: transparent !important;
    box-shadow: none;

    &:hover {
      box-shadow: none;
    }
  }

  &--block {
    display: flex;
    flex: 1;
    margin: $button-margin-y 0;
    width: 100%;
  }

  &--round {
    border-radius: 28px;

    &:after {
      border-radius: 28px;
    }
  }
}
</style>
